import { useCallback, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import * as v from 'valibot';
import type { RootState } from '@redux/RootReducer';

import {
  useCargoTypes,
  useCarrierServices,
  useCarriers,
  useCompanyUsers,
  useContractStatuses,
  useCountries,
  useEquipmentTypes,
  usePortTypes,
  usePricingCategories,
  usePricingTypes,
  useStates,
  useSupplierDiversities,
  useTariffTypes,
  useTransportationModes,
} from '@/modules/entities';
import { DEFAULT_MONTH_RANGE, getLocalTimeZone, toZonedDayIso } from '@/utils/date';
import { cleanNuqsURLParams } from '@/utils/url';

import { toFormOption } from '../data-provider';
import {
  type ContractsSearchFiltersFormValues,
  type ContractsSearchParams,
  contractsSearchParamsSchema,
} from '../models';

const parsers = {
  approvedOn: parseAsString,
  cargoType: parseAsString,
  carrierKey: parseAsInteger,
  createdBy: parseAsInteger,
  customerLaneId: parseAsInteger,
  destinationCity: parseAsString,
  destinationCountry: parseAsString,
  destinationPortName: parseAsString,
  destinationPortType: parseAsInteger,
  destinationPostalCode: parseAsString,
  destinationState: parseAsString,
  effectiveFrom: parseAsString,
  effectiveTo: parseAsString,
  modifiedBy: parseAsInteger,
  name: parseAsString,
  originCity: parseAsString,
  originCountry: parseAsString,
  originPortName: parseAsString,
  originPortType: parseAsInteger,
  originPostalCode: parseAsString,
  originState: parseAsString,
  pricingBasis: parseAsString,
  pricingCategory: parseAsString,
  pricingType: parseAsString,
  serviceKey: parseAsInteger,
  status: parseAsString,
  supplierDiversity: parseAsString,
  trailerKey: parseAsInteger,
  transportationMode: parseAsString,
  uniqueName: parseAsString,
};

export const getDefaultFormValues = (timeZone = getLocalTimeZone()): ContractsSearchFiltersFormValues => {
  return {
    approvedOn: undefined,
    cargoType: null,
    carrierKey: null,
    contractName: null,
    createdBy: null,
    customerLaneId: null,
    destinationCity: null,
    destinationCountry: null,
    destinationPortName: null,
    destinationPortType: null,
    destinationPostalCode: null,
    destinationState: null,
    effectiveFrom: toZonedDayIso(timeZone),
    effectiveTo: toZonedDayIso(timeZone, DEFAULT_MONTH_RANGE, true),
    modifiedBy: null,
    originCity: null,
    originCountry: null,
    originPortName: null,
    originPortType: null,
    originPostalCode: null,
    originState: null,
    pricingBasis: null,
    pricingCategory: null,
    pricingType: null,
    serviceKey: null,
    status: null,
    supplierDiversity: null,
    trailerKey: null,
    transportationMode: null,
    uniqueName: null,
  };
};

export const useContractSearchFilters = () => {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);
  const [rawFilters, setFilters] = useQueryStates(parsers, { history: 'replace' });

  const cleanedRawFilters = useMemo(() => {
    const result = v.safeParse(contractsSearchParamsSchema, cleanNuqsURLParams(rawFilters));

    if (!result.success) {
      return {} as ContractsSearchParams;
    }

    return result.output;
  }, [rawFilters]);

  const [isSearchSubmitted, setIsSearchSubmitted] = useState(() =>
    Object.values(cleanedRawFilters).some((value) => value !== undefined && value !== null),
  );

  const customerKey = selectedCompany?.companyId ?? 0;

  const contractsSearchParams = useMemo(
    () => ({
      ...cleanedRawFilters,
      customerKey,
    }),
    [cleanedRawFilters, customerKey],
  );

  const { data: carriers } = useCarriers({
    searchParams: {
      ids: cleanedRawFilters?.carrierKey ? [String(cleanedRawFilters.carrierKey)] : [],
    },
  });
  const { data: cargoTypes } = useCargoTypes({
    searchParams: {
      ids: cleanedRawFilters?.cargoType ? [String(cleanedRawFilters.cargoType)] : [],
    },
  });
  const { data: pricingTypes } = usePricingTypes({
    searchParams: {
      ids: cleanedRawFilters?.pricingType ? [String(cleanedRawFilters.pricingType)] : [],
    },
  });
  const { data: tariffTypes } = useTariffTypes({
    searchParams: {
      ids: cleanedRawFilters?.pricingBasis ? [String(cleanedRawFilters.pricingBasis)] : [],
    },
  });
  const { data: pricingCategories } = usePricingCategories({
    searchParams: {
      ids: cleanedRawFilters?.pricingCategory ? [String(cleanedRawFilters.pricingCategory)] : [],
    },
  });
  const { data: supplierDiversities } = useSupplierDiversities({
    searchParams: {
      ids: cleanedRawFilters?.supplierDiversity ? [String(cleanedRawFilters.supplierDiversity)] : [],
    },
  });
  const { data: equipmentTypes } = useEquipmentTypes({
    searchParams: {
      ids: cleanedRawFilters?.trailerKey ? [String(cleanedRawFilters.trailerKey)] : [],
    },
  });
  const { data: carrierServices } = useCarrierServices({
    searchParams: {
      ids: cleanedRawFilters?.serviceKey ? [String(cleanedRawFilters.serviceKey)] : [],
    },
  });
  const { data: transportationModes } = useTransportationModes({
    searchParams: {
      ids: cleanedRawFilters?.transportationMode ? [String(cleanedRawFilters.transportationMode)] : [],
    },
  });
  const { data: contractStatuses } = useContractStatuses();
  const { data: countries } = useCountries({
    searchParams: {
      ids: [cleanedRawFilters?.originCountry, cleanedRawFilters?.destinationCountry].filter(Boolean).map(String),
    },
  });
  const { data: portTypes } = usePortTypes({
    searchParams: {
      ids: [cleanedRawFilters?.originPortType, cleanedRawFilters?.destinationPortType]
        .filter((id) => id !== null && id !== undefined)
        .map(String),
    },
  });
  const { data: originStates } = useStates({
    CountryCode: cleanedRawFilters?.originCountry ?? '',
    searchParams: {
      ids: cleanedRawFilters?.originState ? [cleanedRawFilters.originState] : [],
    },
  });
  const { data: destinationStates } = useStates({
    CountryCode: cleanedRawFilters?.destinationCountry ?? '',
    searchParams: {
      ids: cleanedRawFilters?.destinationState ? [cleanedRawFilters.destinationState] : [],
    },
  });
  const { data: companyUsers } = useCompanyUsers({
    companyId: String(customerKey),
    searchParams: {
      ids: [cleanedRawFilters?.createdBy, cleanedRawFilters?.modifiedBy]
        .filter((id) => id !== null && id !== undefined)
        .map(String),
    },
  });

  const formValues: ContractsSearchFiltersFormValues = useMemo(() => {
    return {
      approvedOn: cleanedRawFilters?.approvedOn,
      cargoType: toFormOption(
        cleanedRawFilters?.cargoType,
        cargoTypes?.map?.[String(cleanedRawFilters?.cargoType)]?.label,
      ),
      carrierKey: toFormOption(
        cleanedRawFilters?.carrierKey,
        carriers?.map?.[String(cleanedRawFilters?.carrierKey)]?.carrierName,
      ),
      contractName: cleanedRawFilters?.name ?? null,
      createdBy: toFormOption(
        cleanedRawFilters?.createdBy,
        companyUsers?.map?.[String(cleanedRawFilters?.createdBy)]?.label,
      ),
      customerLaneId: cleanedRawFilters?.customerLaneId != null ? String(cleanedRawFilters.customerLaneId) : null,
      destinationCity: cleanedRawFilters?.destinationCity ?? null,
      destinationCountry: countries?.map?.[String(cleanedRawFilters?.destinationCountry)] ?? null,
      destinationPortName: cleanedRawFilters?.destinationPortName ?? null,
      destinationPortType: toFormOption(
        cleanedRawFilters?.destinationPortType,
        portTypes?.map?.[String(cleanedRawFilters?.destinationPortType)]?.label,
      ),
      destinationPostalCode: cleanedRawFilters?.destinationPostalCode ?? null,
      destinationState: toFormOption(
        cleanedRawFilters?.destinationState,
        destinationStates?.map?.[String(cleanedRawFilters?.destinationState)]?.label,
      ),
      effectiveFrom: cleanedRawFilters?.effectiveFrom,
      effectiveTo: cleanedRawFilters?.effectiveTo,
      modifiedBy: toFormOption(
        cleanedRawFilters?.modifiedBy,
        companyUsers?.map?.[String(cleanedRawFilters?.modifiedBy)]?.label,
      ),
      originCity: cleanedRawFilters?.originCity ?? null,
      originCountry: countries?.map?.[String(cleanedRawFilters?.originCountry)] ?? null,
      originPortName: cleanedRawFilters?.originPortName ?? null,
      originPortType: toFormOption(
        cleanedRawFilters?.originPortType,
        portTypes?.map?.[String(cleanedRawFilters?.originPortType)]?.label,
      ),
      originPostalCode: cleanedRawFilters?.originPostalCode ?? null,
      originState: toFormOption(
        cleanedRawFilters?.originState,
        originStates?.map?.[String(cleanedRawFilters?.originState)]?.label,
      ),
      pricingBasis: toFormOption(
        cleanedRawFilters?.pricingBasis,
        tariffTypes?.map?.[String(cleanedRawFilters?.pricingBasis)]?.label,
      ),
      pricingCategory: toFormOption(
        cleanedRawFilters?.pricingCategory,
        pricingCategories?.map?.[String(cleanedRawFilters?.pricingCategory)]?.label,
      ),
      pricingType: toFormOption(
        cleanedRawFilters?.pricingType,
        pricingTypes?.map?.[String(cleanedRawFilters?.pricingType)]?.label,
      ),
      serviceKey: toFormOption(
        cleanedRawFilters?.serviceKey,
        carrierServices?.map?.[String(cleanedRawFilters?.serviceKey)]?.label,
      ),
      status: toFormOption(
        cleanedRawFilters?.status,
        contractStatuses?.map?.[String(cleanedRawFilters?.status)]?.label,
      ),
      supplierDiversity: toFormOption(
        cleanedRawFilters?.supplierDiversity,
        supplierDiversities?.map?.[String(cleanedRawFilters?.supplierDiversity)]?.label,
      ),
      trailerKey: toFormOption(
        cleanedRawFilters?.trailerKey,
        equipmentTypes?.map?.[String(cleanedRawFilters?.trailerKey)]?.label,
      ),
      transportationMode: toFormOption(
        cleanedRawFilters?.transportationMode,
        transportationModes?.map?.[String(cleanedRawFilters?.transportationMode)]?.label,
      ),
      uniqueName: cleanedRawFilters?.uniqueName ?? null,
    };
  }, [
    cleanedRawFilters,
    cargoTypes,
    carriers,
    carrierServices,
    companyUsers,
    contractStatuses,
    countries,
    destinationStates,
    equipmentTypes,
    originStates,
    portTypes,
    pricingCategories,
    pricingTypes,
    supplierDiversities,
    tariffTypes,
    transportationModes,
  ]);

  const updateFilters: typeof setFilters = (newFilters) => {
    setIsSearchSubmitted(true);

    return setFilters(
      Object.entries(newFilters as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (value !== undefined && value !== '') {
          acc[key] = value;
        } else {
          acc[key] = null;
        }
        return acc;
      }, {}) as Parameters<typeof setFilters>[0],
    );
  };

  const clearFilters = useCallback(() => {
    setIsSearchSubmitted(false);

    return setFilters(
      Object.fromEntries(Object.keys(parsers).map((key) => [key, null])) as Parameters<typeof setFilters>[0],
    );
  }, [setFilters]);

  return {
    clearFilters,
    contractsSearchParams,
    customerKey,
    formValues,
    isSearchSubmitted,
    setFilters: updateFilters,
  };
};
